import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";

import { getGsQoOptions, getJrCqOptions, generateGsJr } from "./jrV2Slice";
import { Colors } from "../../../assets/Colors";

// Popup Generate JR General Service: satu JR untuk Periode + Quotation + CQ.
// Customer read-only, mengikuti QO terpilih (user client hanya menerima QO
// miliknya sendiri dari backend). Aturan CQ sama dengan form JR operasional:
// wajib bila customer punya CQ aktif.
export default function GenerateGsModal({ initialPeriod, onClose, onGenerated }) {
  const dispatch = useDispatch();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isClient = currentUser?.Tipe === "Customer";

  const [period, setPeriod] = useState(initialPeriod);
  const [qoList, setQoList] = useState([]);
  const [qoLoading, setQoLoading] = useState(false);
  const [qoNo, setQoNo] = useState("");
  const [cqList, setCqList] = useState([]);
  const [cqID, setCqID] = useState(0);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Muat QO tiap kali periode berubah; QO terpilih dipertahankan bila masih berlaku.
  useEffect(() => {
    if (!period) { setQoList([]); return; }
    setQoLoading(true);
    dispatch(getGsQoOptions(period)).unwrap()
      .then(res => {
        const list = res?.data ?? [];
        setQoList(list);
        setQoNo(prev => {
          if (list.some(q => q.QoNo === prev)) return prev;
          return list.length === 1 ? list[0].QoNo : "";
        });
      })
      .catch(() => setQoList([]))
      .finally(() => setQoLoading(false));
  }, [period]); // eslint-disable-line

  const selectedQo = qoList.find(q => q.QoNo === qoNo);
  const klienID = selectedQo?.KlienID ?? "";
  // Client: nama customer sudah pasti miliknya meski QO belum dipilih.
  const namaKlien = selectedQo?.NamaKlien || (isClient ? qoList[0]?.NamaKlien : "") || "";

  // Muat CQ milik customer QO terpilih.
  useEffect(() => {
    setCqID(0);
    if (!klienID) { setCqList([]); return; }
    dispatch(getJrCqOptions(klienID)).unwrap()
      .then(res => {
        const list = res?.data ?? [];
        setCqList(list);
        if (list.length === 1) setCqID(list[0].CQID);
      })
      .catch(() => setCqList([]));
  }, [klienID]); // eslint-disable-line

  const qoOptions = useMemo(() => qoList.map(q => ({
    value: q.QoNo,
    label: `${q.QoNo}${q.NoKontrak ? " · " + q.NoKontrak : ""}${isClient ? "" : " — " + q.NamaKlien}`,
  })), [qoList, isClient]);

  const cqOptions = useMemo(() => cqList.map(c => ({
    value: c.CQID,
    label: c.NamaCQ ? `${c.KodeCQ} — ${c.NamaCQ}` : c.KodeCQ,
  })), [cqList]);
  const cqRequired = cqList.length > 0;

  const handleGenerate = async () => {
    const e = {};
    if (!period) e.period = "Wajib diisi";
    if (!qoNo) e.qoNo = "Pilih quotation";
    if (cqRequired && !cqID) e.cqID = "Pilih CQ";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const res = await dispatch(generateGsJr({ period, qoNo, cqID })).unwrap();
      toast.success(res?.message ?? "JR General Service berhasil dibuat");
      onGenerated?.(res?.data);
    } catch (err) {
      toast.error(typeof err === "string" ? err : (err?.message ?? "Gagal generate JR General Service"));
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "block text-xs font-bold uppercase text-gray-600 mb-1";
  const selectPortal = { menuPortalTarget: document.body, styles: { menuPortal: b => ({ ...b, zIndex: 9999 }) } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => !saving && onClose()}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Generate JR General Service</h3>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Periode <span className="text-red-500">*</span></label>
            <input type="month" value={period} onChange={e => setPeriod(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm outline-none" />
            {errors.period && <p className="text-red-500 text-xs mt-1">{errors.period}</p>}
          </div>

          <div>
            <label className={labelCls}>Quotation <span className="text-red-500">*</span></label>
            <Select {...selectPortal}
              isLoading={qoLoading}
              value={qoOptions.filter(o => o.value === qoNo)}
              onChange={o => { setQoNo(o?.value ?? ""); setErrors(p => ({ ...p, qoNo: undefined })); }}
              options={qoOptions}
              placeholder={qoLoading ? "Memuat…" : "Pilih quotation…"}
              noOptionsMessage={() => "Tidak ada quotation General Service yang berlaku di periode ini"} />
            {errors.qoNo && <p className="text-red-500 text-xs mt-1">{errors.qoNo}</p>}
          </div>

          <div>
            <label className={labelCls}>Customer</label>
            <input type="text" readOnly value={namaKlien} placeholder="Mengikuti quotation"
              className="w-full border rounded px-3 py-2 text-sm bg-gray-100 text-gray-700 outline-none" />
          </div>

          <div>
            <label className={labelCls}>CQ {cqRequired && <span className="text-red-500">*</span>}</label>
            <Select {...selectPortal}
              isSearchable
              isDisabled={!klienID || cqOptions.length === 0}
              isClearable={!cqRequired}
              value={cqOptions.filter(o => o.value === cqID)}
              onChange={o => { setCqID(o?.value ?? 0); setErrors(p => ({ ...p, cqID: undefined })); }}
              options={cqOptions}
              placeholder={!klienID ? "Pilih quotation dahulu"
                : cqOptions.length === 0 ? "Customer tidak punya CQ" : "Cari / pilih CQ…"} />
            {errors.cqID && <p className="text-red-500 text-xs mt-1">{errors.cqID}</p>}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-600 hover:bg-gray-100">
            Batal
          </button>
          <button onClick={handleGenerate} disabled={saving}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ background: Colors.primary }}>
            {saving ? "Memproses…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
}
